import {
  CurrentStockResponse,
  Product,
  QuantityUnit,
  Recipe,
  RecipeIngredient,
  ShoppingListItem,
} from "utils/grocyApi.ts";
import { UnitConversionService } from "../../services/UnitConversionService.ts";
import { buildRecipeFulfillmentStatus, RecipeFulfillmentStatus } from "./recipeFulfillment.ts";

interface BuildSortedRecipeStateOptions {
  recipes: Recipe[];
  recipeIngredients: RecipeIngredient[];
  stock: CurrentStockResponse[];
  products: Product[];
  quantityUnits: QuantityUnit[];
  shoppingListItems: ShoppingListItem[];
  conversionService: UnitConversionService | null;
}

function getMissingIngredientCount(
  recipeId: number,
  recipeIngredients: RecipeIngredient[],
  products: Product[],
  stock: CurrentStockResponse[],
  conversionService: UnitConversionService | null,
): number {
  return recipeIngredients
    .filter((ingredient) => ingredient.recipe_id === recipeId)
    .filter((ingredient) => {
      const product = products.find((productItem) => productItem.id === ingredient.product_id);
      if (!product) return true;

      return conversionService
        ? !conversionService.getIngredientStockComparison(ingredient, product).isAvailable
        : ((stock.find((stockItem) => stockItem.product_id === ingredient.product_id)?.amount || 0) < ingredient.amount);
    }).length;
}

export function buildSortedRecipeState({
  recipes,
  recipeIngredients,
  stock,
  products,
  quantityUnits,
  shoppingListItems,
  conversionService,
}: BuildSortedRecipeStateOptions): {
  sortedRecipes: Recipe[];
  recipeStockStatus: Map<number, RecipeFulfillmentStatus>;
} {
  const normalRecipes = recipes.filter((recipe) => recipe.type === "normal");
  const baseSortedRecipes = [...normalRecipes].sort((a, b) => {
    const missingA = getMissingIngredientCount(
      a.id,
      recipeIngredients,
      products,
      stock,
      conversionService,
    );
    const missingB = getMissingIngredientCount(
      b.id,
      recipeIngredients,
      products,
      stock,
      conversionService,
    );

    if (missingA === 0 && missingB !== 0) return -1;
    if (missingA !== 0 && missingB === 0) return 1;
    if (missingA !== missingB) return missingA - missingB;
    return a.name.localeCompare(b.name);
  });

  const recipeStockStatus = buildRecipeFulfillmentStatus({
    recipes: baseSortedRecipes,
    recipeIngredients,
    products,
    quantityUnits,
    stock,
    shoppingListItems,
    conversionService,
  });

  const sortedRecipes = [...baseSortedRecipes].sort((a, b) => {
    const statusA = recipeStockStatus.get(a.id);
    const statusB = recipeStockStatus.get(b.id);

    if (!statusA || !statusB) return 0;

    if (statusA.isFullyAvailable && !statusB.isFullyAvailable) return -1;
    if (!statusA.isFullyAvailable && statusB.isFullyAvailable) return 1;

    if (statusA.isFullyCoveredByShoppingList && !statusB.isFullyCoveredByShoppingList) return -1;
    if (!statusA.isFullyCoveredByShoppingList && statusB.isFullyCoveredByShoppingList) return 1;

    if (statusA.isPartiallyCoveredByShoppingList && !statusB.isPartiallyCoveredByShoppingList) return -1;
    if (!statusA.isPartiallyCoveredByShoppingList && statusB.isPartiallyCoveredByShoppingList) return 1;

    if (statusA.missingIngredients !== statusB.missingIngredients) {
      return statusA.missingIngredients - statusB.missingIngredients;
    }

    return a.name.localeCompare(b.name);
  });

  return { sortedRecipes, recipeStockStatus };
}
